export default class TurboStream extends HTMLElement {
  async connectedCallback () {
    try {
      await this.render()
    } catch (error) {
      console.error(error)
    } finally {
      this.disconnect()
    }
  }

  async render () {
    return (this.renderPromise ??= (async () => {
      this.performAction()
    })())
  }

  disconnect () {
    try {
      this.remove()
    } catch {}
  }

  removeDuplicateTargetChildren () {
    this.duplicateChildren.forEach((c) => c.remove())
  }

  get duplicateChildren () {
    const existingChildren = this.targetElements.flatMap((e) => [...e.children]).filter((c) => !!c.getAttribute('id'))
    const newChildrenIds = [...(this.templateContent?.children || [])].filter((c) => !!c.getAttribute('id')).map((c) => c.getAttribute('id'))
    return existingChildren.filter((c) => newChildrenIds.includes(c.getAttribute('id')))
  }

  get performAction () {
    switch (this.action) {
      case 'after':
        return () => {
          this.targetElements.forEach((e) => e.parentElement?.insertBefore(this.templateContent, e.nextSibling))
        }
      case 'append':
        return () => {
          this.removeDuplicateTargetChildren()
          this.targetElements.forEach((e) => e.append(this.templateContent))
        }
      case 'before':
        return () => {
          this.targetElements.forEach((e) => e.parentElement?.insertBefore(this.templateContent, e))
        }
      case 'prepend':
        return () => {
          this.removeDuplicateTargetChildren()
          this.targetElements.forEach((e) => e.prepend(this.templateContent))
        }
      case 'remove':
        return () => {
          this.targetElements.forEach((e) => e.remove())
        }
      case 'replace':
        return () => {
          this.targetElements.forEach((targetElement) => {
            targetElement.replaceWith(this.templateContent)
          })
        }
      case 'update':
        return () => {
          this.targetElements.forEach((targetElement) => {
            targetElement.innerHTML = ''
            targetElement.append(this.templateContent)
          })
        }
      default:
        this.raise('unknown or missing action')
    }
  }

  get targetElements () {
    if (this.target) {
      return this.targetElementsById
    } else if (this.targets) {
      return this.targetElementsByQuery
    } else {
      this.raise('target or targets attribute is missing')
    }
  }

  get templateContent () {
    return this.templateElement.content.cloneNode(true)
  }

  get templateElement () {
    if (this.firstElementChild === null) {
      const template = this.ownerDocument.createElement('template')
      this.appendChild(template)
      return template
    } else if (this.firstElementChild instanceof HTMLTemplateElement) {
      return this.firstElementChild
    }
    this.raise('first child element must be a <template> element')
  }

  get action () {
    return this.getAttribute('action')
  }

  get target () {
    return this.getAttribute('target')
  }

  get targets () {
    return this.getAttribute('targets')
  }

  get requestId () {
    return this.getAttribute('request-id')
  }

  raise (message) {
    throw new Error(`${this.description}: ${message}`)
  }

  get description () {
    return (this.outerHTML.match(/<[^>]+>/) ?? [])[0] ?? '<turbo-stream>'
  }

  get targetElementsById () {
    const element = this.ownerDocument?.getElementById(this.target)
    return element !== null ? [element] : []
  }

  get targetElementsByQuery () {
    const elements = this.ownerDocument?.querySelectorAll(this.targets)
    return elements.length !== 0 ? Array.prototype.slice.call(elements) : []
  }
}
